import { useEffect, useRef, useState } from "preact/hooks";
import type { WalletSessionValue } from "@tenergy/web-core/wallet";
import { setConfirmHandler, type ConfirmChallenge } from "../api/request.js";
import { SHOW_LEDGER_TRON } from "./ledger-flags.js";
import { loadLedger } from "../session/use-admin-session.js";
import { Button, Note } from "../ui.js";

interface Pending {
  challenge: ConfirmChallenge;
  resolve(signature: string): void;
  reject(error: unknown): void;
}

/**
 * The confirmation of a dangerous action (docs/product/admin-auth.md §6).
 *
 * The API wrote the text: the action and its facts (amount, address, old -> new), the admin, the time, a nonce. It is
 * signed here by the admin's CONFIRMATION KEY — not by this browser — and the request goes again with the signature.
 * A script in this page cannot approve anything on its own: it would need the key, which is on the device.
 *
 * The Ledger Ethereum app shows this very text on its screen, so it is marked the safest. The TRON app shows only a
 * hash, and a browser wallet signs on this computer; both are marked less safe (owner 2026-10-31).
 */
export function ConfirmDialog({ wallet }: { wallet: WalletSessionValue }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setConfirmHandler(
      (challenge) =>
        new Promise<string>((resolve, reject) => {
          setError(null);
          setPending({ challenge, resolve, reject });
        }),
    );
    return () => setConfirmHandler(null);
  }, []);

  useEffect(() => {
    if (pending === null) return undefined;
    dialogRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) cancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  if (pending === null) return null;
  const { challenge } = pending;

  function cancel() {
    pending?.reject(new Error("cancelled"));
    setPending(null);
  }

  async function sign(how: () => Promise<string>) {
    setBusy(true);
    setError(null);
    try {
      const signature = await how();
      pending!.resolve(signature);
      setPending(null);
    } catch (failure) {
      const ledger = await loadLedger().catch(() => null);
      setError(ledger?.describeLedgerError(failure) ?? (failure instanceof Error ? failure.message : "The signature did not finish."));
    } finally {
      setBusy(false);
    }
  }

  const withLedger = (app: "ethereum" | "tron", transport: "usb" | "ble") =>
    sign(async () => {
      const ledger = await loadLedger();
      // The key may be any of the device's accounts: found by its address, never assumed to be the first.
      const { link, account } = await ledger.withLedger(transport, async (link) => ({
        link,
        account: await ledger.findLedgerAccount(link, app, challenge.keyAddress),
      }));
      return ledger.ledgerSignMessage(link, account, challenge.message);
    });

  const withWallet = () =>
    sign(async () => {
      const address = wallet.address ?? (await wallet.requestConnection());
      if (address !== challenge.keyAddress) {
        throw new Error(`The wallet is on ${address}, not the confirmation key ${challenge.keyAddress}.`);
      }
      return wallet.signMessage(challenge.message);
    });

  const ethereum = challenge.keyKind === "ethereum";

  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        class="max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-[20px] border border-line bg-surface p-5 outline-none"
      >
        <h2 id="confirm-title" class="font-display text-[17px] font-extrabold tracking-[-0.01em] text-ink">
          Confirm this action
        </h2>
        <p class="mt-1 text-[11.5px] leading-relaxed text-ink-muted">
          Check every line, then sign it with your confirmation key{" "}
          <span class="num break-all text-ink">{challenge.keyAddress}</span>
          {challenge.target === "nile" ? " (Nile)" : ""}.
        </p>
        <pre class="num mt-3 overflow-x-auto rounded-[12px] border border-line bg-surface-2 p-3 text-[11px] leading-relaxed whitespace-pre-wrap break-all text-ink">
          {challenge.message}
        </pre>
        <div class="mt-3">
          {ethereum ? (
            <Note>Safest: the Ledger Ethereum app shows this exact text. Compare it with the lines above before you approve.</Note>
          ) : (
            <Note tone="warning">
              Less safe: a TRON key cannot show this text on a Ledger (the TRON app shows only a hash), and a browser wallet
              signs on this computer. A Ledger with the Ethereum app as the confirmation key is the safest; set it under
              Security.
            </Note>
          )}
        </div>
        {error === null ? null : (
          <div class="mt-3">
            <Note tone="danger">{error}</Note>
          </div>
        )}
        <div class="mt-4 flex flex-wrap gap-2">
          {ethereum ? (
            <>
              <Button disabled={busy} onClick={() => void withLedger("ethereum", "usb")}>
                Sign on Ledger · USB
              </Button>
              <Button tone="ghost" disabled={busy} onClick={() => void withLedger("ethereum", "ble")}>
                Ledger · Bluetooth
              </Button>
            </>
          ) : (
            <>
              <Button disabled={busy} onClick={() => void withWallet()}>
                Sign in wallet
              </Button>
              {SHOW_LEDGER_TRON ? (
                <>
                  <Button tone="ghost" disabled={busy} onClick={() => void withLedger("tron", "usb")}>
                    Ledger TRON · USB
                  </Button>
                  <Button tone="ghost" disabled={busy} onClick={() => void withLedger("tron", "ble")}>
                    Ledger TRON · Bluetooth
                  </Button>
                </>
              ) : null}
            </>
          )}
          <Button tone="ghost" disabled={busy} class="ml-auto" onClick={cancel}>
            Cancel
          </Button>
        </div>
        {busy ? <p class="mt-2 text-[11px] text-ink-muted">Confirm on the device or in the wallet…</p> : null}
      </div>
    </div>
  );
}
